import React, { useEffect } from 'react';
import { useLoader } from '@react-three/fiber';
import { OBJLoader } from 'three-stdlib';
import * as THREE from 'three';
import { API_BASE_URL } from '../../constants/config';
import { TrajectoryInfo } from '../../types/types';
import {
  knownTrajectoryColors,
  extractColorFromFilename,
} from '../../utils/trajectoryColor';

interface TrajectoryModelProps {
  deploymentFolder: string;
  fileName: string;
  rotation: THREE.Euler;
  selectedSeason: string;
  selectedInfo: TrajectoryInfo | null;
  onClick: () => void;
}

const colorPositionsWinter: { [key: string]: THREE.Vector3 } = {
  blue: new THREE.Vector3(-81, -140, -24),
  yellow: new THREE.Vector3(-991, 127, -21),
  orange: new THREE.Vector3(841, 136, -39),
  green: new THREE.Vector3(243, -24, -45),
  magenta: new THREE.Vector3(1731, 118, -38),
  red: new THREE.Vector3(-166, -40, -25),
};

const colorPositionsSummer: { [key: string]: THREE.Vector3 } = {
  blue: new THREE.Vector3(-180, -270, 25),
  yellow: new THREE.Vector3(-1080, 0, 50),
  orange: new THREE.Vector3(750, -10, 15),
  green: new THREE.Vector3(150, -160, 10),
  magenta: new THREE.Vector3(1650, -16, 40),
  red: new THREE.Vector3(-260, -180, 27),
};

const TrajectoryModel: React.FC<TrajectoryModelProps> = ({
  deploymentFolder,
  fileName,
  selectedInfo,
  selectedSeason,
  rotation,
  onClick,
}) => {
  const trajectoryObj = useLoader(
    OBJLoader,
    `${API_BASE_URL}/api/model/trajectories/${deploymentFolder}/${fileName}`
  );

  const color = extractColorFromFilename(fileName);

  const deploymentNumber = parseInt(deploymentFolder.split('_')[1]);

  let position =
    (selectedSeason === 'winter'
      ? colorPositionsWinter[color]
      : colorPositionsSummer[color]) || new THREE.Vector3(0, 0, 0);

  if (selectedSeason === 'winter') {
    if (deploymentNumber === 5 && color === 'green') {
      position = new THREE.Vector3(475, -80, -45);
    } else if (deploymentNumber === 6 && color === 'blue') {
      position = new THREE.Vector3(-81, -60, -24);
    } else if (deploymentNumber >= 7 && color === 'green') {
      position = new THREE.Vector3(390, -20, -30);
    } else if (deploymentNumber >= 4 && color === 'yellow') {
      position = new THREE.Vector3(-930, 127, -21);
    }
  }

  if (selectedSeason === 'summer') {
    if (deploymentNumber === 5 && color === 'green') {
      position = new THREE.Vector3(385, -230, 0);
    } else if (deploymentNumber === 6 && color === 'blue') {
      position = new THREE.Vector3(-175, -195, 25);
    } else if (deploymentNumber >= 7 && color === 'green') {
      position = new THREE.Vector3(300, -160, 15);
    } else if (deploymentNumber >= 4 && color === 'yellow') {
      position = new THREE.Vector3(-1030, 0, 50);
    }
  }

  trajectoryObj.scale.set(2, 2, 2);
  const box = new THREE.Box3().setFromObject(trajectoryObj);
  const center = box.getCenter(new THREE.Vector3());

  trajectoryObj.position.sub(center);
  trajectoryObj.position.add(position);

  trajectoryObj.rotation.copy(rotation);

  trajectoryObj.traverse((child: any) => {
    if (child.isMesh) {
      child.material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(
          color in knownTrajectoryColors
            ? knownTrajectoryColors[color as keyof typeof knownTrajectoryColors]
            : 'gray'
        ),
      });
      child.castShadow = false;
      child.receiveShadow = false;
    } else if (child.isPoints) {
      child.material = new THREE.PointsMaterial({
        color: new THREE.Color(
          color in knownTrajectoryColors
            ? knownTrajectoryColors[color as keyof typeof knownTrajectoryColors]
            : 'gray'
        ),
        size: 20,
        sizeAttenuation: true,
      });
      child.castShadow = false;
      child.receiveShadow = false;
    }
  });

  useEffect(() => {
    if (selectedInfo?.color === color) {
      trajectoryObj.traverse((child: any) => {
        if (child.isPoints) {
          child.material.size = 35;
          child.material.needsUpdate = true;
        }
      });
    } else {
      trajectoryObj.traverse((child: any) => {
        if (child.isPoints) {
          child.material.size = 14;
          child.material.needsUpdate = true;
        }
      });
    }
  }, [selectedInfo]);

  const handleClick = (event: any) => {
    event.stopPropagation();
    onClick();
  };

  return <primitive object={trajectoryObj} onClick={handleClick} />;
};

export default TrajectoryModel;
